import { Laptop, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type LaptopSystemAdmin,
  useAdminLaptopSystems,
  useCreateLaptopSystem,
  useDeleteLaptopSystem,
  useUpdateLaptopSystem,
} from "@/hooks/use-laptop-systems";
import { type User, useUsers } from "@/hooks/use-users";
import { normalizeBookmarkUrl } from "@/lib/office/laptop-bookmarks";
import { cn } from "@/lib/utils";
import { createLaptopSystemSchema } from "@/schemas/laptop-system";

type EditorState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; system: LaptopSystemAdmin };

type AssignState = { type: "none" } | { type: "assign"; system: LaptopSystemAdmin };

export function LaptopSystemsPanel() {
  const { data: systems = [], isLoading, isError, error, refetch } = useAdminLaptopSystems();
  const createSystem = useCreateLaptopSystem();
  const updateSystem = useUpdateLaptopSystem();
  const deleteSystem = useDeleteLaptopSystem();
  const [editor, setEditor] = useState<EditorState>({ type: "none" });
  const [assign, setAssign] = useState<AssignState>({ type: "none" });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load laptop systems"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Manage software links that appear on teammates&apos; desk laptops in HQ. Mark a system as{" "}
          <strong>Everyone</strong> so all users see it, or assign it to specific people.
        </p>
        <Button type="button" onClick={() => setEditor({ type: "create" })}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add system
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">System</th>
              <th className="px-4 py-3 font-medium">Audience</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? ["a", "b", "c", "d"].map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td className="px-4 py-3" colSpan={4}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </td>
                  </tr>
                ))
              : null}
            {!isLoading && systems.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8">
                  <EmptyState
                    icon={Laptop}
                    title="No systems yet"
                    description="Add Slack, GitHub, or internal tools so they appear on desk laptops."
                  />
                </td>
              </tr>
            ) : null}
            {systems.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white"
                      style={{ background: s.color }}
                    >
                      {s.name.slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground">{s.name}</p>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-xs text-primary hover:underline"
                      >
                        {s.url}
                      </a>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {s.isGlobal ? (
                    <Badge variant="secondary">Everyone</Badge>
                  ) : (
                    <span className="text-muted-foreground">
                      {s.assignedUserIds.length} user
                      {s.assignedUserIds.length === 1 ? "" : "s"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={s.active ? "default" : "outline"}>
                    {s.active ? "Active" : "Hidden"}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setAssign({ type: "assign", system: s })}
                      title="Assign people"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditor({ type: "edit", system: s })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(s.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SystemEditorDialog
        state={editor}
        onOpenChange={(open) => {
          if (!open) setEditor({ type: "none" });
        }}
        onCreate={async (input) => {
          await createSystem.mutateAsync(input);
          setEditor({ type: "none" });
        }}
        onUpdate={async (id, input) => {
          await updateSystem.mutateAsync({ id, ...input });
          setEditor({ type: "none" });
        }}
        isSaving={createSystem.isPending || updateSystem.isPending}
      />

      <AssignUsersDialog
        state={assign}
        onOpenChange={(open) => {
          if (!open) setAssign({ type: "none" });
        }}
        onSave={async (systemId, assignedUserIds, isGlobal) => {
          await updateSystem.mutateAsync({ id: systemId, assignedUserIds, isGlobal });
          setAssign({ type: "none" });
        }}
        isSaving={updateSystem.isPending}
      />

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        title="Delete system?"
        description="This removes the system from every laptop and clears assignments. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (!deleteId) return;
          await deleteSystem.mutateAsync(deleteId);
          setDeleteId(null);
        }}
        loading={deleteSystem.isPending}
      />
    </div>
  );
}

function SystemEditorDialog({
  state,
  onOpenChange,
  onCreate,
  onUpdate,
  isSaving,
}: {
  state: EditorState;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: {
    name: string;
    url: string;
    color: string;
    description: string | null;
    sortOrder: number;
    isGlobal: boolean;
    active: boolean;
  }) => Promise<void>;
  onUpdate: (
    id: string,
    input: {
      name: string;
      url: string;
      color: string;
      description: string | null;
      sortOrder: number;
      isGlobal: boolean;
      active: boolean;
    },
  ) => Promise<void>;
  isSaving: boolean;
}) {
  const open = state.type !== "none";
  const existing = state.type === "edit" ? state.system : null;
  const [name, setName] = useState("");
  const [url, setUrl] = useState("https://");
  const [color, setColor] = useState("#1E6CC7");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isGlobal, setIsGlobal] = useState(false);
  const [active, setActive] = useState(true);

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name);
      setUrl(existing.url);
      setColor(existing.color);
      setDescription(existing.description ?? "");
      setSortOrder(existing.sortOrder);
      setIsGlobal(existing.isGlobal);
      setActive(existing.active);
    } else {
      setName("");
      setUrl("https://");
      setColor("#1E6CC7");
      setDescription("");
      setSortOrder(0);
      setIsGlobal(false);
      setActive(true);
    }
  }, [open, existing]);

  const [formError, setFormError] = useState<string | null>(null);

  async function handleSave() {
    const payload = {
      name: name.trim(),
      url: normalizeBookmarkUrl(url),
      color,
      description: description.trim() || null,
      sortOrder,
      isGlobal,
      active,
    };
    const parsed = createLaptopSystemSchema.safeParse(payload);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Invalid form");
      return;
    }
    setFormError(null);
    const data = {
      name: parsed.data.name,
      url: parsed.data.url,
      color: parsed.data.color,
      description: parsed.data.description ?? null,
      sortOrder: parsed.data.sortOrder,
      isGlobal: parsed.data.isGlobal,
      active: parsed.data.active,
    };
    if (existing) await onUpdate(existing.id, data);
    else await onCreate(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit system" : "Add system"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={48} />
          </div>
          <div className="space-y-1.5">
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} maxLength={2000} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                />
                <Input value={color} onChange={(e) => setColor(e.target.value)} maxLength={7} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                min={0}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              placeholder="Short hint for teammates"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isGlobal}
              onChange={(e) => setIsGlobal(e.target.checked)}
              className="rounded border-border"
            />
            Everyone (all users see this on their laptop)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-border"
            />
            Active (visible on laptops)
          </label>
          {formError ? <p className="text-xs font-medium text-destructive">{formError}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignUsersDialog({
  state,
  onOpenChange,
  onSave,
  isSaving,
}: {
  state: AssignState;
  onOpenChange: (open: boolean) => void;
  onSave: (systemId: string, userIds: string[], isGlobal: boolean) => Promise<void>;
  isSaving: boolean;
}) {
  const open = state.type === "assign";
  const system = state.type === "assign" ? state.system : null;
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 250);
  const { data: users = [], isLoading } = useUsers({
    search: open ? debounced : undefined,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isGlobal, setIsGlobal] = useState(false);

  useEffect(() => {
    if (!open || !system) return;
    setSelected(new Set(system.assignedUserIds));
    setIsGlobal(system.isGlobal);
    setSearch("");
  }, [open, system]);

  const selectedList = useMemo(() => [...selected], [selected]);

  /** Labels for chips: prefer known assignedUsers, then search results. */
  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    if (system) {
      for (const u of system.assignedUsers) {
        map.set(u.id, u.name || u.email);
      }
    }
    for (const u of users) {
      map.set(u.id, u.name || u.email);
    }
    return map;
  }, [system, users]);

  function toggle(user: User) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(user.id)) next.delete(user.id);
      else next.add(user.id);
      return next;
    });
  }

  function removeId(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign people — {system?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={isGlobal}
              onChange={(e) => setIsGlobal(e.target.checked)}
              className="rounded border-border"
            />
            <span>
              <span className="font-semibold">Everyone</span>
              <span className="block text-xs text-muted-foreground">
                Show for all users (individual picks still saved for later)
              </span>
            </span>
          </label>
          {selectedList.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {selectedList.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => removeId(id)}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs font-medium hover:bg-muted"
                  title="Remove"
                >
                  {labelById.get(id) ?? id.slice(0, 8)}
                  <span className="text-muted-foreground">×</span>
                </button>
              ))}
            </div>
          ) : null}
          <SearchInput value={search} onValueChange={setSearch} placeholder="Search teammates…" />
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
            {isLoading ? (
              <p className="p-2 text-sm text-muted-foreground">Loading users…</p>
            ) : users.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">No users found.</p>
            ) : (
              users.map((u) => {
                const on = selected.has(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted/60",
                      on && "bg-primary/10",
                    )}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={on}
                      className="rounded border-border"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{u.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {u.email}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isGlobal
              ? "Everyone will see this system."
              : `${selectedList.length} person${selectedList.length === 1 ? "" : "s"} selected.`}
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSaving || !system}
            onClick={() => {
              if (!system) return;
              void onSave(system.id, selectedList, isGlobal);
            }}
          >
            {isSaving ? "Saving…" : "Save assignments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
